import React, { useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import server from '../global/server';

// De quanto em quanto tempo a lista se atualiza sozinha. Trinta segundos e o
// bastante para um aviso de transmissao chegar enquanto ainda vale a pena
// entrar, sem transformar o sininho num pinga-pinga de requisicoes.
const INTERVALO = 30000;

const quandoFoi = data => {
    const minutos = Math.floor((Date.now() - new Date(data).getTime()) / 60000);
    if (minutos < 1) return 'agora';
    if (minutos < 60) return `há ${minutos} min`;
    const horas = Math.floor(minutos / 60);
    if (horas < 24) return `há ${horas}h`;
    const dias = Math.floor(horas / 24);
    return dias === 1 ? 'ontem' : `há ${dias} dias`;
};

/**
 * Sininho de avisos.
 *
 * Mostra o que aconteceu com os anfitriões que o conviva acompanha: encontro
 * novo, horário novo e transmissão começando -- e a mensagem que chegou pelo
 * canal interno do encontro. O clique leva para onde a ação
 * acontece — a tela do encontro, para comprar, ou a da transmissão, para
 * assistir — e marca o aviso como lido no caminho.
 */
const SininhoDeAvisos = () => {
    const navigate = useNavigate();
    const [ancora, setAncora] = useState(null);
    // SWR direto, e nao useFetch: aquele hook liga o loading global, e a barra
    // de progresso do topo piscaria a cada consulta do sininho
    const { data, mutate } = useSWR(
        '/notificacoes',
        async url => (await server.get(url)).data,
        { refreshInterval: INTERVALO, revalidateOnFocus: true, errorRetryCount: 1 },
    );

    const avisos = data?.data?.avisos || [];
    const naoLidos = data?.data?.nao_lidos || 0;

    const marcarLidos = async ids => {
        try {
            await server.put('/notificacoes/lidas', ids ? { ids } : {});
            mutate();
        } catch (erro) {
            // Falhar em marcar como lido nao pode atrapalhar a navegacao: o
            // aviso continua la e o proximo clique tenta de novo
            mutate();
        }
    };

    const abrir = aviso => {
        setAncora(null);

        // O aviso sai da lista na hora, sem esperar o servidor: a navegacao
        // acontece em seguida, e voltar e reencontrar o aviso ja atendido seria
        // um recado repetido. O mutate abaixo confirma com o servidor depois.
        mutate(
            atual =>
                atual && {
                    ...atual,
                    data: {
                        avisos: (atual.data?.avisos || []).filter(item => item.id !== aviso.id),
                        nao_lidos: Math.max(0, (atual.data?.nao_lidos || 1) - 1),
                    },
                },
            { revalidate: false },
        );

        marcarLidos([aviso.id]);
        navigate(aviso.link);
    };

    return (
        <>
            <IconButton onClick={evento => setAncora(evento.currentTarget)} aria-label="Avisos">
                <Badge badgeContent={naoLidos} color="error" max={9}>
                    <NotificationsNoneOutlinedIcon />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={ancora}
                open={Boolean(ancora)}
                onClose={() => setAncora(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{ sx: { width: 340, maxWidth: '92vw', borderRadius: '12px' } }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 2, py: 1 }}
                >
                    <Typography variant="subtitle2" fontWeight={700}>
                        Avisos
                    </Typography>
                    {naoLidos > 0 && (
                        <Button
                            size="small"
                            onClick={() => marcarLidos()}
                            sx={{ textTransform: 'none', color: '#006a61' }}
                        >
                            Marcar tudo como lido
                        </Button>
                    )}
                </Stack>

                <Divider />

                {avisos.length === 0 ? (
                    <Box sx={{ px: 2, py: 3 }}>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Nenhum aviso novo. Acompanhe um anfitrião para saber quando ele criar um
                            encontro ou entrar ao vivo.
                        </Typography>
                    </Box>
                ) : (
                    avisos.map(aviso => (
                        <MenuItem
                            key={aviso.id}
                            onClick={() => abrir(aviso)}
                            sx={{
                                alignItems: 'flex-start',
                                gap: 1.5,
                                py: 1.25,
                                whiteSpace: 'normal',
                            }}
                        >
                            {aviso.type === 'transmissao' ? (
                                <LiveTvIcon sx={{ color: '#c62828', mt: 0.25 }} />
                            ) : aviso.type === 'mensagem' ? (
                                <ChatBubbleOutlineIcon sx={{ color: '#1976d2', mt: 0.25 }} />
                            ) : (
                                <EventAvailableIcon sx={{ color: '#006a61', mt: 0.25 }} />
                            )}

                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600}>
                                    {aviso.title}
                                </Typography>
                                {aviso.body && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {aviso.body}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.disabled">
                                    {quandoFoi(aviso.createdAt)}
                                </Typography>
                            </Box>
                        </MenuItem>
                    ))
                )}
            </Menu>
        </>
    );
};

export default SininhoDeAvisos;
